import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Save, Trash2, UserRoundPlus, X } from 'lucide-react';
import type { CreateSupervisorInput, SupervisorSummary, UpdateSupervisorInput } from '@ailan/contracts';

type Props = {
  supervisors: SupervisorSummary[];
  open: boolean;
  saving: boolean;
  onClose: () => void;
  onCreate: (input: CreateSupervisorInput) => void;
  onUpdate: (id: string, input: UpdateSupervisorInput) => void;
  onDelete: (id: string) => void;
  onAssign: (agentId: string, supervisorId: string) => void;
};

type Draft = { name: string; targetSatisfaction: number };

export function SupervisorDialog({ supervisors, open, saving, onClose, onCreate, onUpdate, onDelete, onAssign }: Props) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [name, setName] = useState('');
  const [targetSatisfaction, setTargetSatisfaction] = useState(95);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});

  useEffect(() => {
    setDrafts(Object.fromEntries(supervisors.map((supervisor) => [supervisor.id, {
      name: supervisor.name,
      targetSatisfaction: supervisor.targetSatisfaction,
    }])));
  }, [supervisors]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim()) return;
    onCreate({ name: name.trim(), targetSatisfaction });
    setName('');
    setTargetSatisfaction(95);
  };

  const agents = supervisors.flatMap((supervisor) => supervisor.agents.map((agent) => ({
    ...agent,
    supervisorId: supervisor.id,
  })));

  return (
    <dialog ref={dialogRef} className="supervisorDialog" onClose={onClose}>
      <div>
        <header className="dialogHead"><h2>主管与客服归属</h2><button type="button" className="iconButton" onClick={onClose} aria-label="关闭"><X /></button></header>
        <div className="dialogBody supervisorBody">
          <form className="supervisorCreate" onSubmit={submit}>
            <label>主管姓名<input value={name} maxLength={80} onChange={(event) => setName(event.target.value)} /></label>
            <label>满意度目标<div className="unitInput"><input type="number" min="0" max="100" step="0.1" value={targetSatisfaction} onChange={(event) => setTargetSatisfaction(Number(event.target.value))} /><span>%</span></div></label>
            <button className="button primary" disabled={saving || !name.trim()}><UserRoundPlus />新增主管</button>
          </form>

          <section className="supervisorSection">
            <h3>主管设置</h3>
            <div className="supervisorRows">
              {supervisors.map((supervisor) => {
                const draft = drafts[supervisor.id] ?? { name: supervisor.name, targetSatisfaction: supervisor.targetSatisfaction };
                return <div className="supervisorEditRow" key={supervisor.id}>
                  <input aria-label={`${supervisor.name}姓名`} value={draft.name} onChange={(event) => setDrafts({ ...drafts, [supervisor.id]: { ...draft, name: event.target.value } })} />
                  <div className="unitInput"><input aria-label={`${supervisor.name}满意度目标`} type="number" min="0" max="100" step="0.1" value={draft.targetSatisfaction} onChange={(event) => setDrafts({ ...drafts, [supervisor.id]: { ...draft, targetSatisfaction: Number(event.target.value) } })} /><span>%</span></div>
                  <span>{supervisor.metrics.agentCount} 名客服</span>
                  <button type="button" className="iconButton bordered" title="保存主管设置" disabled={saving || !draft.name.trim()} onClick={() => onUpdate(supervisor.id, draft)}><Save /></button>
                  <button type="button" className="iconButton bordered deleteButton" title={supervisor.id === 'default-supervisor' ? '默认主管不能删除' : supervisor.metrics.agentCount ? '请先转移名下客服' : '删除主管'} disabled={saving || supervisor.id === 'default-supervisor' || supervisor.metrics.agentCount > 0} onClick={() => onDelete(supervisor.id)}><Trash2 /></button>
                </div>;
              })}
            </div>
          </section>

          <section className="supervisorSection">
            <h3>客服归属</h3>
            <div className="assignmentTable">
              <div className="assignmentHead"><span>客服</span><span>所属主管</span></div>
              {agents.map((agent) => <div className="assignmentRow" key={agent.id}><strong>{agent.name}</strong><select value={agent.supervisorId} disabled={saving} onChange={(event) => onAssign(agent.id, event.target.value)}>{supervisors.map((supervisor) => <option value={supervisor.id} key={supervisor.id}>{supervisor.name}</option>)}</select></div>)}
            </div>
          </section>
        </div>
        <footer className="dialogFoot"><button type="button" className="button" onClick={onClose}>关闭</button></footer>
      </div>
    </dialog>
  );
}
